import { Box } from '@mui/material'
import {
    MyRostersQuery,
    ProcessStatusGroup,
    RosterStatus,
    RosterType,
} from '__generated__/graphql'
import { RosterListProps } from 'client/pages/RosterSearches/ClientRosterCardPreview'
import { DetailsRowProps } from 'client/pages/RosterSearches/DetailsRow'
import { useState, type FC } from 'react'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import RosterDrawer from 'recruiter/components/RosterDrawer'
import { MyRecruiterRosters } from 'recruiter/graphql/rosters.gql'
import RostersByStatusList from 'shared/components/RostersByStatusList'
import { usePagination } from 'shared/hooks/usePagination'
import { GroupedStatus, RosterResults } from 'shared/models/groupedStatus'
import RosterCard from './RosterCard'

const defaultPageLimit = 10
const savedFilterToken = 'recruiter.searches.activeFilter'
const trackedDiv = 'tracked-roster-preview'

const MyRosters: FC = () => {
    usePageTitle('Searches')
    const [createRosterDrawerOpen, setCreateRosterDrawerOpen] = useState(false)
    const activeRostersPagination = usePagination<
        MyRostersQuery['recruiterRosters']['data'][number]
    >({
        query: MyRecruiterRosters,
        variables: {
            rosterStatuses: [RosterStatus.Active, RosterStatus.Opportunistic],
            limit: defaultPageLimit,
        },
        queryPath: 'recruiterRosters',
        limit: defaultPageLimit,
        trackedDivClassName: `${trackedDiv}-all`,
    })

    const onHoldRostersPagination = usePagination<
        MyRostersQuery['recruiterRosters']['data'][number]
    >({
        query: MyRecruiterRosters,
        variables: {
            rosterStatuses: [RosterStatus.OnHold],
            limit: defaultPageLimit,
        },
        queryPath: 'recruiterRosters',
        limit: defaultPageLimit,
        trackedDivClassName: `${trackedDiv}-onhold`,
    })

    const draftRostersPagination = usePagination<
        MyRostersQuery['recruiterRosters']['data'][number]
    >({
        query: MyRecruiterRosters,
        variables: {
            rosterStatuses: [RosterStatus.Draft],
            limit: defaultPageLimit,
        },
        queryPath: 'recruiterRosters',
        limit: defaultPageLimit,
        trackedDivClassName: `${trackedDiv}-draft`,
    })

    const closedRostersPagination = usePagination<
        MyRostersQuery['recruiterRosters']['data'][number]
    >({
        query: MyRecruiterRosters,
        variables: {
            rosterStatuses: [RosterStatus.Closed],
            limit: defaultPageLimit,
        },
        queryPath: 'recruiterRosters',
        limit: defaultPageLimit,
        trackedDivClassName: `${trackedDiv}-closed`,
    })

    const getPaginationForFilter = (filter: GroupedStatus) => {
        switch (filter.status) {
            case RosterStatus.Draft:
                return {
                    ...draftRostersPagination,
                    trackedDivClassName:
                        draftRostersPagination.trackedDivClassName ||
                        trackedDiv,
                }
            case RosterStatus.Closed:
                return {
                    ...closedRostersPagination,
                    trackedDivClassName:
                        closedRostersPagination.trackedDivClassName ||
                        trackedDiv,
                }
            case RosterStatus.OnHold:
                return {
                    ...onHoldRostersPagination,
                    trackedDivClassName:
                        onHoldRostersPagination.trackedDivClassName ||
                        trackedDiv,
                }
            case RosterStatus.Active:
            case RosterStatus.Opportunistic:
            default:
                return {
                    ...activeRostersPagination,
                    trackedDivClassName:
                        activeRostersPagination.trackedDivClassName ||
                        trackedDiv,
                }
        }
    }

    const getCards = (data: RosterResults[number]) => {
        return <RosterCard {...getRosterPreviewProps(data)} />
    }

    return (
        <Box>
            <RostersByStatusList
                statuses={[
                    {
                        status: RosterStatus.Active,
                    },
                    {
                        status: RosterStatus.OnHold,
                    },
                    {
                        status: RosterStatus.Draft,
                    },
                    {
                        status: RosterStatus.Closed,
                    },
                ]}
                pageTitle={'My Searches'}
                getPaginationForFilter={getPaginationForFilter}
                saveFilterKey={savedFilterToken}
                addButton={{
                    onClick: () => {
                        setCreateRosterDrawerOpen(true)
                    },
                    label: '+ New Search',
                }}
            >
                {getCards}
            </RostersByStatusList>
            {createRosterDrawerOpen && (
                <RosterDrawer
                    selectedCandidates={[]}
                    type={'new'}
                    onClose={() => {
                        setCreateRosterDrawerOpen(false)
                    }}
                />
            )}
        </Box>
    )
}

const getRosterPreviewProps = (
    rosterData: MyRostersQuery['recruiterRosters']['data'][number] | undefined
): RosterListProps => {
    const details: DetailsRowProps = {
        sharedRecruiters:
            rosterData?.sharedRecruiters?.map(({ recruiter }) => ({
                id: recruiter?.id || '',
                preferredName: recruiter?.user?.preferredName || '',
                profileImageKey: recruiter?.user?.profileImageKey || '',
            })) || [],
        sharedClients:
            rosterData?.sharedClients?.map(({ client }) => ({
                id: client?.id || '',
                preferredName: client?.user?.preferredName || '',
                profileImageKey: client?.user?.profileImageKey || '',
            })) || [],
        positions:
            rosterData?.placements?.map(
                (placement) => placement?.title?.value ?? ''
            ) || [],
        startDateLabel: rosterData?.startDateLabel ?? '',
        officeLocations:
            rosterData?.companyOffices?.map(
                (office) => office?.companyOffice?.officeLocation?.value ?? ''
            ) || [],
        industries: rosterData?.industries?.map(
            (industry) => industry?.industry?.value ?? ''
        ),
        sectors: rosterData?.sectors?.map(
            (sector) => sector?.sector?.value ?? ''
        ),
        hideOnShrink: true,
        company: rosterData?.company?.value ?? '',
    }
    return {
        details,
        rosterName: rosterData?.rosterName,
        rosterType: rosterData?.rosterType ?? RosterType.Mapping,
        rosterId: rosterData?.id ?? '',
        statusGroupsForGraph: Object.values(ProcessStatusGroup),
    }
}

export default MyRosters
